import { fetchWithAuth } from "../utils/fetchWithAuth";

// Constants
const DEFAULT_CLIENT_TYPE = "individual";

// API Endpoints
const API_ENDPOINTS = {
  TEMPLATES: {
    FOLDERS: "/api/templates/template-folders",
    BY_ID: (id) => `/api/templates/template/${id}`,
    GENERATE_DOCUMENT: "/api/templates/generate-document",
    CONVERT_HTML: "/api/templates/convert-json-to-html",
    DEBUG: (id) => `/api/templates/template/${id}/debug`,
    GET_HTML: "/api/templates/get-template-html",
  },
  ADVISERS: {
    BRANCH: "/api/advisers/branch",
    DETAILS: "/api/advisers/details",
  },
};

// Error Messages
const ERROR_MESSAGES = {
  API_REQUEST_FAILED: "API request failed",
  INVALID_RESPONSE: "Invalid response from server",
};

/**
 * API Error Handler - Centralized error handling for API responses
 */
class ApiErrorHandler {
  /**
   * Parse error response from API and return meaningful error message
   * @param {Response} response - The fetch response object
   * @returns {Promise<string>} Parsed error message
   */
  static async parseApiError(response) {
    try {
      const errorData = await response.json();
      return errorData.detail || errorData.message || `HTTP error! status: ${response.status}`;
    } catch (parseError) {
      return `HTTP error! status: ${response.status}`;
    }
  }

  /**
   * Handle API request with standardized error handling
   * @param {Function} apiCall - The API call function to execute
   * @param {string} operationName - Name of the operation for logging
   * @returns {Promise<any>} API response data
   * @throws {Error} When API request fails
   */
  static async handleApiRequest(apiCall, operationName) {
    try {
      const response = await apiCall();
      if (!response.ok) {
        const errorMessage = await this.parseApiError(response);
        throw new Error(`[${operationName}] ${errorMessage}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error in ${operationName}:`, error);
      throw error;
    }
  }
}

/**
 * Base API Client - Provides common API functionality
 */
class BaseApiClient {
  /**
   * Make authenticated API request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Fetch options
   * @returns {Promise<Response>} Fetch response
   */
  async makeRequest(endpoint, options = {}) {
    return fetchWithAuth(endpoint, {
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      ...options,
    });
  }

  /**
   * Make GET request
   * @param {string} endpoint - API endpoint
   * @returns {Promise<any>} Response data
   */
  async get(endpoint) {
    return ApiErrorHandler.handleApiRequest(
      () => this.makeRequest(endpoint, { method: "GET" }),
      `GET ${endpoint}`
    );
  }

  /**
   * Make POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise<any>} Response data
   */
  async post(endpoint, data) {
    return ApiErrorHandler.handleApiRequest(
      () => this.makeRequest(endpoint, {
        method: "POST",
        body: JSON.stringify(data),
      }),
      `POST ${endpoint}`
    );
  }
}

/**
 * Template API Client - Handles template-related operations
 */
class TemplateApiClient extends BaseApiClient {
  /**
   * Get all template folders and their templates
   * @returns {Promise<Array>} Array of template folders with their templates
   */
  async getTemplateFolders() {
    return this.get(API_ENDPOINTS.TEMPLATES.FOLDERS);
  }

  /**
   * Get a template's JSON data by template ID
   * @param {string} templateId - The template ID
   * @returns {Promise<Object>} Template data including JSON
   */
  async getTemplateById(templateId) {
    return this.get(API_ENDPOINTS.TEMPLATES.BY_ID(templateId));
  }

  /**
   * Generate a complete document from template ID and client ID
   * @param {string} templateId - The template ID
   * @param {string} clientId - The client ID
   * @param {string} clientType - The client type ('individual' or 'entity')
   * @param {string} [adviserId] - Optional adviser ID
   * @returns {Promise<Object>} Generated document with processed JSON and HTML
   */
  async generateDocument(templateId, clientId, clientType, adviserId = null) {
    const requestBody = {
      template_id: templateId,
      client_id: clientId,
      client_type: clientType || DEFAULT_CLIENT_TYPE,
    };
    
    if (adviserId) {
      requestBody.adviser_id = adviserId;
    }
    
    return this.post(API_ENDPOINTS.TEMPLATES.GENERATE_DOCUMENT, requestBody);
  }

  /**
   * Convert JSON to HTML without variable replacement
   * @param {Object} templateJson - The template JSON object
   * @returns {Promise<Object>} Object with HTML output
   */
  async convertJsonToHtml(templateJson) {
    return this.post(API_ENDPOINTS.TEMPLATES.CONVERT_HTML, {
      template_json: templateJson,
    });
  }

  /**
   * Debug a template to check its data structure
   * @param {string} templateId - The ID of the template to debug
   * @returns {Promise<Object>} Template debug information
   */
  async debugTemplate(templateId) {
    return this.get(API_ENDPOINTS.TEMPLATES.DEBUG(templateId));
  }

  /**
   * Get the processed HTML for a template with variable replacement
   * @param {string} templateId - The template ID
   * @param {string} clientId - The client ID
   * @param {string} clientType - The client type ('individual' or 'entity')
   * @param {string} [adviserId] - Optional adviser ID
   * @returns {Promise<string>} The processed HTML
   */
  async getTemplateHtml(templateId, clientId, clientType, adviserId = null) {
    const body = {
      template_id: templateId,
      client_id: clientId,
      client_type: clientType,
    };
    
    if (adviserId) {
      body.adviser_id = adviserId;
    }
    
    const response = await this.post(API_ENDPOINTS.TEMPLATES.GET_HTML, body);
    return response.html;
  }
}

/**
 * Adviser API Client - Handles adviser-related operations
 */
class AdviserApiClient extends BaseApiClient {
  /**
   * Get all advisers in the user's branch
   * @returns {Promise<Array>} List of advisers in the branch
   */
  async getBranchAdvisers() {
    return this.get(API_ENDPOINTS.ADVISERS.BRANCH);
  }

  /**
   * Get detailed information for a specific adviser
   * @param {string} adviserId - The adviser ID
   * @param {string} clientId - The client ID (required for access control)
   * @returns {Promise<Object>} Adviser details
   */
  async getAdviserDetails(adviserId, clientId) {
    return this.post(API_ENDPOINTS.ADVISERS.DETAILS, {
      params: {
        adviser_id: adviserId,
        client_id: clientId
      }
    });
  }
}

// Create API client instances
const templateApi = new TemplateApiClient();
const adviserApi = new AdviserApiClient();

// Export individual functions for backward compatibility
export const getTemplateFolders = () => templateApi.getTemplateFolders();
export const getTemplateById = (templateId) => templateApi.getTemplateById(templateId);
export const generateDocument = (templateId, clientId, clientType, adviserId) => 
  templateApi.generateDocument(templateId, clientId, clientType, adviserId);
export const convertJsonToHtml = (templateJson) => templateApi.convertJsonToHtml(templateJson);
export const debugTemplate = (templateId) => templateApi.debugTemplate(templateId);
export const getBranchAdvisers = () => adviserApi.getBranchAdvisers();
export const getAdviserDetails = (adviserId, clientId) => adviserApi.getAdviserDetails(adviserId, clientId);
export const getTemplateHtml = (templateId, clientId, clientType, adviserId) => 
  templateApi.getTemplateHtml(templateId, clientId, clientType, adviserId);

// Export API clients for advanced usage
export { templateApi, adviserApi, ApiErrorHandler }; 